import { FaArrowLeft, FaBolt, FaShieldAlt, FaClock, FaChartLine, FaExclamationTriangle, FaCheckCircle, FaLightbulb } from "react-icons/fa";
import BackendScreenerTable, { ColumnConfig } from "../screener/BackendScreenerTable.tsx";
import PremiumAccessPage from "../PremiumAccessPage.tsx";
import ViewModeToggle from "../common/ViewModeToggle.tsx";
import ChartGridView from "../common/ChartGridView.tsx";
import ScreenerFilterBar from "../common/ScreenerFilterBar.tsx";

import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import '../SwingScreener.css';
import './PositiveGapUpGuide.css';
import { usePremium } from "../../hooks/usePremium.ts";
import useScreenerData from "../../hooks/useScreenerData.ts";
import useScreenerHistory from "../../hooks/useScreenerHistory.ts";
import { ScreenerStockFilters } from "../../services/screenerApi.ts";
import { SCREENER_PAGE_SIZE, SCREENER_HISTORY_PAGE_SIZE } from "../../constants/screener.ts";
import PostCaptureInfo from "../common/PostCaptureInfo.tsx";


const PositiveGapUpScanner = () => {

    const navigate = useNavigate();
    const isPremium = usePremium();
    const [viewMode, setViewMode] = useState<'list' | 'chart'>('list');
    const [insightsViewMode, setInsightsViewMode] = useState<'list' | 'chart'>('list');
    const [activeTab, setActiveTab] = useState<'stocks' | 'insights' | 'howto'>(!isPremium ? 'insights' : 'stocks');
    const [filters, setFilters] = useState<ScreenerStockFilters>({});
    const { data: stocksData, loading: stocksLoading, error: stocksError,
            currentPage, totalPages, totalItems, setCurrentPage } = useScreenerData('POSITIVE_GAP_UP', isPremium, filters);
    const { data: historyData, loading: historyLoading, error: historyError,
            currentPage: historyPage, totalPages: historyTotalPages, totalItems: historyTotalItems,
            setCurrentPage: setHistoryPage } = useScreenerHistory('POSITIVE_GAP_UP');

    // Columns for stocks tab
    const stocksColumns: ColumnConfig[] = [
        { key: 'stockName', label: 'Stock Name', visible: true },
        { key: 'symbol', label: 'Symbol', visible: true },
        { key: 'capturedPrice', label: 'Captured Price', visible: true },
        { key: 'capturedOn', label: 'Captured On', visible: true },
    ];

    const historyColumns: ColumnConfig[] = [
        { key: 'stockName', label: 'Stock Name', visible: true },
        { key: 'symbol', label: 'Symbol', visible: true },
        { key: 'capturedPrice', label: 'Captured Price', visible: true },
        { key: 'currentPrice', label: 'Current Price', visible: true },
        { key: 'maxUpside', label: 'Max Upside %', visible: true },
        { key: 'capturedOn', label: 'Captured On', visible: true },
    ];

    return (
        <>
            <div className="main-content">
                {/* Header */}
                <div className="chart-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FaArrowLeft />
                    </button>
                    <h2 className="chart-title">Positive Gap Up 🔥</h2>
                </div>

                {/* Accuracy banner — hidden temporarily */}
                {/* <div className="accuracy-banner">
                    <span className="accuracy-dot" />
                    <div className="accuracy-badge">
                        <span className="accuracy-badge-icon">🎯</span>
                        ~74% Accuracy
                    </div>
                    <div className="accuracy-banner-text">
                        <span className="accuracy-banner-title">AI-Verified Pattern Accuracy</span>
                        <span className="accuracy-banner-sub">Based on historical price patterns observed over the last 3 months.</span>
                    </div>
                </div> */}

                {/* Tabs */}
                <div className="scanner-tabs">
                    <button
                        className={`scanner-tab ${activeTab === 'stocks' ? 'active' : ''}`}
                        onClick={() => setActiveTab('stocks')}
                    >
                        Stocks
                    </button>
                    {/* Post Capture Insights tab — hidden temporarily */}
                    {/* <button
                        className={`scanner-tab ${activeTab === 'insights' ? 'active' : ''}`}
                        onClick={() => setActiveTab('insights')}
                    >
                        Post Capture Insights (Backtest)
                    </button> */}
                    <button
                        className={`scanner-tab ${activeTab === 'howto' ? 'active' : ''}`}
                        onClick={() => setActiveTab('howto')}
                    >
                        How to? 💡
                    </button>
                </div>

                {/* View Mode Toggle + Filters - stocks tab */}
                {activeTab === 'stocks' && isPremium && (
                    <div className="screener-toolbar">
                        <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
                        <div className="screener-toolbar-filter">
                            <ScreenerFilterBar onApply={setFilters} />
                        </div>
                    </div>
                )}

                {/* View Mode Toggle - insights/backtest tab */}
                {activeTab === 'insights' && (
                    <ViewModeToggle viewMode={insightsViewMode} onViewModeChange={setInsightsViewMode} />
                )}

                {/* Stocks Tab */}
                {activeTab === 'stocks' && (
                    !isPremium ? (<PremiumAccessPage />) : (
                        viewMode === 'list' ? (
                            <BackendScreenerTable
                                screenerCode="POSITIVE_GAP_UP"
                                pageSize={SCREENER_PAGE_SIZE}
                                columns={stocksColumns}
                                viewChartButtonEnable={false}
                                data={stocksData}
                                loading={stocksLoading}
                                error={stocksError}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                            />
                        ) : (
                            <ChartGridView
                                isActive={activeTab === 'stocks' && viewMode === 'chart'}
                                data={stocksData}
                                loading={stocksLoading}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                pageSize={SCREENER_PAGE_SIZE}
                                onPageChange={setCurrentPage}

                                screenerCode="POSITIVE_GAP_UP"
                                smaValues={[21, 50]}
                            />
                        )
                    )
                )}

                {/* Insights / Backtest Tab */}
                {activeTab === 'insights' && (
                    <>
                    <PostCaptureInfo onViewLive={() => setActiveTab('stocks')} />
                    {insightsViewMode === 'list' ? (
                        <BackendScreenerTable
                            screenerCode="POSITIVE_GAP_UP"
                            pageSize={SCREENER_HISTORY_PAGE_SIZE}
                            columns={historyColumns}
                            viewChartButtonEnable={false}
                            data={historyData}
                            loading={historyLoading}
                            error={historyError}
                            currentPage={historyPage}
                            totalPages={historyTotalPages}
                            totalItems={historyTotalItems}
                            onPageChange={setHistoryPage}
                        />
                    ) : (
                        <ChartGridView
                            isActive={activeTab === 'insights' && insightsViewMode === 'chart'}
                            data={historyData}
                            loading={historyLoading}
                            currentPage={historyPage}
                            totalPages={historyTotalPages}
                            totalItems={historyTotalItems}
                            pageSize={SCREENER_HISTORY_PAGE_SIZE}
                            onPageChange={setHistoryPage}
                            isAIScreener={true}
                            screenerCode="POSITIVE_GAP_UP"
                            smaValues={[21, 50]}
                        />
                    )}
                    </>
                )}

                {/* How to Tab */}
                {activeTab === 'howto' && (
                    <div className="howto-container">

                        {/* Hero */}
                        <div className="howto-hero">
                            <div className="howto-hero-icon">🔥</div>
                            <h1 className="howto-hero-title">Positive Gap Up Strategy</h1>
                            <p className="howto-hero-subtitle">
                                A high-momentum breakout strategy designed to capture stocks that gap up with strong volume — signalling institutional buying and bullish continuation.
                            </p>
                        </div>

                        {/* What is it */}
                        <div className="howto-section">
                            <div className="howto-section-header">
                                <FaLightbulb className="howto-section-icon" />
                                <h2>What is a Positive Gap Up?</h2>
                            </div>
                            <p className="howto-text">
                                A Positive Gap Up occurs when a stock opens significantly higher than its previous day's close — but not all gap ups are equal. Our screener goes far beyond just detecting a price gap. It filters and validates each opportunity across multiple dimensions that most retail traders simply cannot track manually.
                            </p>

                            <div className="whatisit-grid">
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">📰</span>
                                    <div>
                                        <strong>News & Catalyst Driven</strong>
                                        <p>Gap ups backed by positive catalysts — earnings beats, new contracts, product launches, regulatory approvals or management announcements — have significantly higher follow-through probability. Our screener prioritises stocks where the gap is driven by a fundamental trigger, not random noise.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">🏦</span>
                                    <div>
                                        <strong>Big Player & Institutional Activity</strong>
                                        <p>When mutual funds, FIIs, or large operators accumulate a stock, it leaves footprints in price and volume. We detect unusual volume surges that indicate big money entering — giving you a chance to ride alongside institutions instead of fighting them.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">📦</span>
                                    <div>
                                        <strong>Block Deals & Bulk Orders</strong>
                                        <p>Block deals and bulk orders on exchanges often precede strong price moves. Our algorithm cross-references gap up events with days where significant block deal activity was reported — filtering only the setups where serious capital was deployed.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">📊</span>
                                    <div>
                                        <strong>Volume & Price Structure Validation</strong>
                                        <p>A gap up without volume is a trap. Our screener validates that the gap is supported by above-average volume, and that the stock is breaking out from a proper base or consolidation zone — not just a random spike from an oversold bounce.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">🎯</span>
                                    <div>
                                        <strong>Sector & Market Alignment</strong>
                                        <p>Stocks that gap up in line with their sector momentum have much better odds. We check whether the broader sector is in an uptrend before shortlisting a stock — ensuring you're not swimming against the tide.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">🔬</span>
                                    <div>
                                        <strong>Advanced Multi-Filter Engine</strong>
                                        <p>Behind the scenes, each stock passes through a proprietary multi-layer filter — checking price structure, relative strength, delivery percentage, historical gap performance, and more. What you see in the list is the result of hundreds of calculations that would take hours to replicate manually.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="whatisit-summary">
                                <span>💡</span>
                                <p>In short — this screener does in seconds what would take an experienced analyst hours to research. It surfaces only the highest-quality gap up setups so you can focus on execution, not research.</p>
                            </div>
                        </div>

                        {/* Rules Grid */}
                        <div className="howto-rules-grid">

                            {/* Entry */}
                            <div className="howto-rule-card entry">
                                <div className="howto-rule-header">
                                    <FaBolt className="rule-icon" />
                                    <h3>Commonly Studied Entry Zone</h3>
                                </div>
                                <div className="howto-rule-body">
                                    <div className="rule-item">
                                        <span className="rule-label">Observed Breakout Zone</span>
                                        <span className="rule-value">~1% above captured price</span>
                                    </div>
                                    <div className="rule-example">
                                        <span className="example-label">Illustrative Example</span>
                                        <div className="example-box">
                                            <div className="example-row">
                                                <span>Captured Price</span>
                                                <strong>₹500</strong>
                                            </div>
                                            <div className="example-row highlight">
                                                <span>Observed zone at</span>
                                                <strong>₹505 (~1% above)</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="rule-note">
                                        In breakout trading literature, a common observation is that traders wait for ~1% confirmation above a reference price before acting. This is shared purely for educational study of how breakout patterns are typically analysed — it does not constitute a buy recommendation or financial advice.
                                    </p>
                                </div>
                            </div>

                            {/* Stoploss */}
                            <div className="howto-rule-card stoploss">
                                <div className="howto-rule-header">
                                    <FaShieldAlt className="rule-icon" />
                                    <h3>Risk Reference Levels</h3>
                                </div>
                                <div className="howto-rule-body">
                                    <div className="rule-item">
                                        <span className="rule-label">Common Risk Ref. (Primary)</span>
                                        <span className="rule-value">Previous swing low</span>
                                    </div>
                                    <div className="rule-item">
                                        <span className="rule-label">Common Risk Ref. (Secondary)</span>
                                        <span className="rule-value">5% – 5.5% from entry zone</span>
                                    </div>
                                    <div className="rule-example">
                                        <span className="example-label">Illustrative Example</span>
                                        <div className="example-box">
                                            <div className="example-row">
                                                <span>Entry Zone Reference</span>
                                                <strong>₹505</strong>
                                            </div>
                                            <div className="example-row danger">
                                                <span>Secondary risk level at</span>
                                                <strong>₹477.5 (-5.5%)</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="rule-note">
                                        In technical analysis study, the previous swing low is a commonly referenced structural level. The 5–5.5% figure is a secondary reference used when no clear structural level is nearby. These are educational references only.
                                    </p>
                                </div>
                            </div>

                            {/* Timing */}
                            <div className="howto-rule-card timing">
                                <div className="howto-rule-header">
                                    <FaClock className="rule-icon" />
                                    <h3>Momentum Observation Window</h3>
                                </div>
                                <div className="howto-rule-body">
                                    <div className="rule-item">
                                        <span className="rule-label">Commonly Observed Window</span>
                                        <span className="rule-value">8–10 days post capture</span>
                                    </div>
                                    <div className="rule-item warning">
                                        <span className="rule-label">Beyond this window</span>
                                        <span className="rule-value">Momentum typically fades</span>
                                    </div>
                                    <div className="warning-box">
                                        <FaExclamationTriangle />
                                        <p>Academic study of gap-up patterns generally shows that setups which do not follow through within 8–10 days tend to exhibit <strong>declining momentum</strong>. This observation is shared for educational reference only and is not a directive to take any action.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Target */}
                            <div className="howto-rule-card target">
                                <div className="howto-rule-header">
                                    <FaChartLine className="rule-icon" />
                                    <h3>Profit Zone Observations</h3>
                                </div>
                                <div className="howto-rule-body">
                                    <div className="rule-item success">
                                        <span className="rule-label">Commonly Studied Approach</span>
                                        <span className="rule-value">Trailing reference, no fixed target</span>
                                    </div>
                                    <div className="rule-item">
                                        <span className="rule-label">Partial Exit Reference</span>
                                        <span className="rule-value">~50% qty at 8–10% move</span>
                                    </div>
                                    <div className="success-box">
                                        <FaCheckCircle />
                                        <p>In breakout pattern studies, traders often reference a trailing stop approach rather than a fixed exit. Booking partial quantity (~50%) near an 8–10% move is a commonly documented risk-management observation — not a recommendation or advice.</p>
                                    </div>
                                    <div className="rule-example">
                                        <span className="example-label">Illustrative Example</span>
                                        <div className="example-box">
                                            <div className="example-row">
                                                <span>Hypothetical entry (100 qty)</span>
                                                <strong>₹505</strong>
                                            </div>
                                            <div className="example-row success">
                                                <span>Partial reference exit (50 qty)</span>
                                                <strong>₹555 (~10%)</strong>
                                            </div>
                                            <div className="example-row highlight">
                                                <span>Remaining 50 qty observation</span>
                                                <strong>Trailing reference 📈</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Summary Cheatsheet */}
                        <div className="howto-cheatsheet">
                            <h3>📋 Pattern Study Summary</h3>
                            <div className="cheatsheet-grid">
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">⚡</span>
                                    <div>
                                        <strong>Entry Zone (observed)</strong>
                                        <span>~1% above captured price</span>
                                    </div>
                                </div>
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">🛡️</span>
                                    <div>
                                        <strong>Risk Ref. (studied)</strong>
                                        <span>Prev swing low or ~5–5.5%</span>
                                    </div>
                                </div>
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">⏱️</span>
                                    <div>
                                        <strong>Momentum Window</strong>
                                        <span>Typically 8–10 days</span>
                                    </div>
                                </div>
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">📈</span>
                                    <div>
                                        <strong>Exit Approach (observed)</strong>
                                        <span>Trailing reference, no fixed target</span>
                                    </div>
                                </div>
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">💰</span>
                                    <div>
                                        <strong>Partial Exit (studied)</strong>
                                        <span>~50% qty near 8–10% move</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* SEBI Disclaimer */}
                        <div className="sebi-disclaimer">
                            <span className="sebi-icon">⚠️</span>
                            <div>
                                <p>SwingSetupAI is not a SEBI registered investment advisor, research analyst, or portfolio manager. All content, screeners, strategies, and information provided on this platform are strictly for educational and informational purposes only and do not constitute investment advice, buy/sell recommendations, or any form of financial guidance. Trading in equities, derivatives, and other financial instruments involves substantial risk of loss. Past performance of any strategy or screener does not guarantee future results. Users are solely responsible for their own investment decisions. Please consult a SEBI registered investment advisor before making any financial decisions.</p>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </>
    );
}

export default PositiveGapUpScanner;
